"use client";
import { useMemo, useState } from "react";

import { useTranslations } from "@zapier/i18n";

import {
  Alert,
  AlertDescription,
  BetaMenuTriggerItem,
  BetaMenuTriggerList,
  BetaMenuTriggerPopover,
  BetaMenuTriggerRoot,
  Button,
  Spinner,
  Text,
  Tooltip,
  TooltipFocusableTrigger,
} from "@zapier/design-system";

import { Icon } from "@zapier/zinnia-icons";

import { HydratedAsset, isCanvasAsset } from "@zapier/client";
import { useEmitFolderDocumentationInteractionEvent } from "@zapier/shared";

import { CanvasEmbed } from "../../CanvasEmbed";
import { FolderNotes } from "./FolderNotes";
import styles from "./FolderDocumentation.module.css";

interface Props {
  folderId: string;
  assets: HydratedAsset[];
  hasEligibleFolderAssetIds: boolean;
  pinnedCanvasId?: string | null;
  isPinnedCanvasIdLoading?: boolean;
  onPinCanvas?: (canvasId: string) => void;
  onUnpinCanvas?: () => void;
  onCreateCanvas?: () => void;
  onGenerateFromFolderAssets?: () => void;
  onRegenerateFromFolderAssets?: () => void;
  isPinning?: boolean;
  isCreating?: boolean;
  isGeneratingFromFolderAssets?: boolean;
  autoLayoutPinnedCanvasOnLoad?: boolean;
  onAutoLayoutPinnedCanvasConsumed?: () => void;
}

export function FolderDocumentation({
  folderId,
  assets,
  hasEligibleFolderAssetIds,
  pinnedCanvasId,
  isPinnedCanvasIdLoading = false,
  onPinCanvas,
  onUnpinCanvas,
  onCreateCanvas,
  onGenerateFromFolderAssets,
  onRegenerateFromFolderAssets,
  isPinning = false,
  isCreating = false,
  isGeneratingFromFolderAssets = false,
  autoLayoutPinnedCanvasOnLoad = false,
  onAutoLayoutPinnedCanvasConsumed,
}: Props) {
  const t = useTranslations("folder-documentation");
  const { emit } = useEmitFolderDocumentationInteractionEvent();

  const generateFromFolderAssetsLabel = t(
    "buttons.generateFromFolderAssets" as const,
  );

  const regenerateCanvasLabel = t("buttons.regenerateCanvas" as const);

  const regenerateDialogTitle = t("regenerateDialog.title" as const);

  const regenerateDialogBody = t("regenerateDialog.body" as const);

  const regenerateDialogCancel = t("regenerateDialog.cancel" as const);

  const regenerateDialogConfirm = t("regenerateDialog.confirm" as const);

  const canvases = useMemo(
    () => assets.filter((asset) => isCanvasAsset(asset)),
    [assets],
  );
  const hasCanvases = canvases.length > 0;
  const pinnedCanvas = pinnedCanvasId
    ? canvases.find((canvas) => canvas.id === pinnedCanvasId)
    : undefined;

  const [isRegenerateAlertOpen, setIsRegenerateAlertOpen] = useState(false);

  // While fetching the pinned canvas id, avoid flashing the empty state UI.
  if (isPinnedCanvasIdLoading) {
    return (
      <div className={styles.root}>
        <div className={styles.emptyState}>
          <Spinner />
        </div>
        <FolderNotes
          folderId={folderId}
          hasEligibleFolderAssetIds={hasEligibleFolderAssetIds}
        />
      </div>
    );
  }

  if (pinnedCanvasId) {
    const pinnedCanvasName = pinnedCanvas?.name || t("pinned.untitled");
    return (
      <div className={styles.root}>
        <div className={styles.pinned}>
          <span className={styles.pinnedTitle}>
            <Text
              tag="p"
              type="SectionHeader"
              className={styles.pinnedTitleText}
            >
              {pinnedCanvasName}
            </Text>
          </span>
          <span className={styles.pinnedActions}>
            <Button
              onPress={() => onUnpinCanvas?.()}
              variant="ghost"
              size="small"
              isDisabled={isPinning || isGeneratingFromFolderAssets}
            >
              {t("pinned.unpin")}
            </Button>
            <Button
              onPress={() => setIsRegenerateAlertOpen(true)}
              variant="tertiary"
              size="small"
              isDisabled={
                isPinning ||
                isGeneratingFromFolderAssets ||
                !hasEligibleFolderAssetIds
              }
            >
              <Icon aria-hidden name="arrowRefresh" />
              {regenerateCanvasLabel}
            </Button>
          </span>
        </div>

        <div className={styles.canvasEmbedWrapper}>
          {isRegenerateAlertOpen && (
            <div className={styles.regenerateAlertOverlay}>
              <Alert
                onDismiss={() => setIsRegenerateAlertOpen(false)}
                status="warning"
                title={regenerateDialogTitle}
              >
                <AlertDescription>{regenerateDialogBody}</AlertDescription>
                <div className={styles.regenerateAlertActions}>
                  <Button
                    variant="tertiary"
                    size="small"
                    onPress={() => setIsRegenerateAlertOpen(false)}
                    isDisabled={isPinning || isGeneratingFromFolderAssets}
                  >
                    {regenerateDialogCancel}
                  </Button>
                  <Button
                    variant="primary"
                    size="small"
                    onPress={() => {
                      setIsRegenerateAlertOpen(false);
                      emit({
                        optionSelected: "regenerate_canvas_from_folder_assets",
                        folderId,
                        canvasId: pinnedCanvasId,
                      });
                      onRegenerateFromFolderAssets?.();
                    }}
                    isDisabled={
                      isPinning ||
                      isGeneratingFromFolderAssets ||
                      !hasEligibleFolderAssetIds
                    }
                  >
                    {regenerateDialogConfirm}
                  </Button>
                </div>
              </Alert>
            </div>
          )}
          <CanvasEmbed
            canvasId={pinnedCanvasId}
            name={pinnedCanvasName}
            folderId={folderId}
            isGeneratingFromFolderAssets={isGeneratingFromFolderAssets}
            autoLayoutOnLoad={autoLayoutPinnedCanvasOnLoad}
            onAutoLayoutConsumed={onAutoLayoutPinnedCanvasConsumed}
          />
        </div>
        <FolderNotes
          folderId={folderId}
          hasEligibleFolderAssetIds={hasEligibleFolderAssetIds}
        />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.emptyState}>
        <Icon aria-hidden name="navCanvas" size={48} color="BrandOrange" />

        <Text tag="h3" type="SectionHeaderMultiline">
          {t("empty.header")}
        </Text>
        <Text tag="p" type="Body1Multiline" color="TextWeaker">
          {t("empty.description")}
        </Text>

        <div className={styles.actions}>
          {hasCanvases ? (
            <BetaMenuTriggerRoot>
              <Button
                variant="tertiary"
                isDisabled={isPinning || isCreating}
                iconPosition="end"
              >
                {t("buttons.pinExistingCanvas" as const)}
                <Icon aria-hidden name="arrowSmallDown" />
              </Button>
              <BetaMenuTriggerPopover>
                <BetaMenuTriggerList
                  aria-label={t("menu.ariaLabel")}
                  onAction={(key) => {
                    onPinCanvas?.(key as string);
                  }}
                >
                  {canvases.map((canvas) => (
                    <BetaMenuTriggerItem
                      key={canvas.id}
                      id={canvas.id}
                      textValue={canvas.name || t("menu.untitledCanvas")}
                    >
                      <Text type="Body1Multiline">
                        {canvas.name || t("menu.untitledCanvas")}
                      </Text>
                    </BetaMenuTriggerItem>
                  ))}
                </BetaMenuTriggerList>
              </BetaMenuTriggerPopover>
            </BetaMenuTriggerRoot>
          ) : (
            <Tooltip
              allowMultiline={true}
              // The translation key exists in `packages/i18n/messages/en/folders.json`,
              // but message key types are generated elsewhere; this cast unblocks TS until regenerated.
              content={t("tooltips.pinExistingCanvasDisabled" as const)}
              placement="bottom"
            >
              <TooltipFocusableTrigger>
                <div className={styles.dropdown}>
                  <Button
                    aria-disabled="true"
                    variant="tertiary"
                    isDisabled={true}
                  >
                    {t("buttons.pinExistingCanvas" as const)}
                    <Icon aria-hidden name="arrowSmallDown" />
                  </Button>
                </div>
              </TooltipFocusableTrigger>
            </Tooltip>
          )}

          <Button
            onPress={() => onCreateCanvas?.()}
            variant="tertiary"
            isDisabled={isCreating || isPinning}
          >
            {t("buttons.createNewCanvas" as const)}
          </Button>

          <Button
            onPress={() => {
              emit({
                optionSelected: "generate_canvas_from_folder_assets",
                folderId,
              });
              onGenerateFromFolderAssets?.();
            }}
            variant="primary"
            isDisabled={
              isCreating ||
              isPinning ||
              isGeneratingFromFolderAssets ||
              !hasEligibleFolderAssetIds
            }
          >
            {generateFromFolderAssetsLabel}
          </Button>
        </div>
      </div>
      <FolderNotes
        folderId={folderId}
        hasEligibleFolderAssetIds={hasEligibleFolderAssetIds}
      />
    </div>
  );
}
